import { Component, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { IAppState } from "./store/states/app";
import { combineLatest } from "rxjs";
import {
  selectIsAuthenticated,
  selectIsAuthenticating,
  selectMustReacceptRules,
  selectRegistrationCompleted,
  selectAuthenticatedUserMe,
} from "./store/selectors";
import { BidUpdateService } from "./services/bid-update.service";
import { AuctionsTrackingService } from "./services/auctions-tracking.service";
import { RoutingStateService } from "./services/routing-state.service";
import Echo from "laravel-echo";
import { LoginAttemptFailed } from "./store/actions/user";
import { ModalService } from "./modules/modal/services/modal.service";
import { ApiService } from "./services/api/api.service";
import { map } from "rxjs/operators";
import {
  Router,
  NavigationStart,
  NavigationEnd,
  Scroll,
} from "@angular/router";
import * as Sentry from "@sentry/angular";

const PUSHER_API_KEY = process.env.PUSHER_API_KEY;
const PUSHER_CLUSTER = process.env.PUSHER_CLUSTER;

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit {
  private authState: { authenticating: boolean; authenticated: boolean } = {
    authenticating: false,
    authenticated: false,
  };

  constructor(
    private _store: Store<IAppState>,
    private bidUpdaterService: BidUpdateService,
    private actionsTrackingService: AuctionsTrackingService,
    private apiService: ApiService,
    routingStateService: RoutingStateService,
    private modalService: ModalService,
    private Router: Router,
  ) {
    combineLatest(
      this._store.select(selectIsAuthenticated),
      this._store.select(selectIsAuthenticating),
    ).subscribe(([authenticated, authenticating]) => {
      this.authState = {
        authenticated: authenticated,
        authenticating: authenticating,
      };
    });

    routingStateService.loadRouting();
  }

  ngOnInit() {
    window.addEventListener("storage", (e) => {
      if (e.key === "auth" && e.newValue === null) {
        this._store.dispatch(
          new LoginAttemptFailed({
            data: null,
            me: null,
            authenticating: false,
            authenticated: false,
            errors: [],
          }),
        );
      }
    });

    // Track user in Sentry
    this._store.select(selectAuthenticatedUserMe).subscribe((userData) => {
      if (!Sentry.getClient()) {
        return;
      }

      if (userData?.id) {
        Sentry.setUser({
          id: userData.id,
        });
      } else {
        Sentry.setUser(null);
      }
    });

    this.startPusher();

    combineLatest([
      this._store.select(selectIsAuthenticated),
      this._store.select(selectRegistrationCompleted),
      this._store.select(selectMustReacceptRules),
      this.Router.events,
    ]).subscribe(
      ([
        authenticated,
        registrationCompleted,
        mustReacceptRules,
        routerEvents,
      ]) => {
        if (
          authenticated &&
          registrationCompleted &&
          mustReacceptRules &&
          routerEvents instanceof Scroll
        ) {
          // this.modalService.closePopup();
          this.modalService.load({
            name: "TermsAndConditionsPopupComponent",
            close: false,
          });
        }
      },
    );
  }

  startPusher() {
    const echo = new Echo({
      broadcaster: "pusher",
      key: PUSHER_API_KEY,
      cluster: PUSHER_CLUSTER,
      forceTLS: true,
    });

    echo
      .channel("bids")
      .listen(".bidding-started", (resp) => {
        this.bidUpdaterService.bidStarted(resp);
      })
      .listen(".bid-created", (resp) => {
        this.bidUpdaterService.updateData(resp);
      });

    echo
      .channel("auctions")
      .listen(".auction-started", (resp) => {
        this.actionsTrackingService.setAuctionStartedData(resp);
      })
      .listen(".auction-ended", (resp) => {
        this.actionsTrackingService.setAuctionEndedData(resp);
      })
      .listen(".auction-updated", ({ data }) => {
        this.actionsTrackingService.setTrackInfo(data);
      });
  }

  /**
   * Return authState to view
   * We use getter on private values [AOT Rule - never use private variable in component view]
   */
  get auth() {
    return this.authState;
  }
}
